import { useEffect, useRef, useState } from 'react';

const useStagger = ({
  delay = 120,
  translateY = 36,
  translateX = 0,
  duration = 1050,
  threshold = 0.08,
  startDelay = 0,
} = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const kids = Array.from(container.children);
    if (!kids.length) return;

    const ease = 'cubic-bezier(0.16, 1, 0.3, 1)';

    kids.forEach((child, i) => {
      child.style.opacity = '0';
      child.style.transform = `translateX(${translateX}px) translateY(${translateY}px)`;
      child.style.transition = `opacity ${duration}ms ${ease} ${startDelay + i * delay}ms, transform ${duration}ms ${ease} ${startDelay + i * delay}ms`;
      child.style.willChange = 'opacity, transform';
    });

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(container);
        }
      },
      { threshold }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [delay, translateY, translateX, duration, threshold, startDelay]);

  useEffect(() => {
    if (!isVisible) return;
    const container = ref.current;
    if (!container) return;

    const kids = Array.from(container.children);
    if (!kids.length) return;

    kids.forEach((child) => {
      child.style.opacity = '1';
      child.style.transform = 'translateX(0px) translateY(0px)';
    });

    const cleanup = startDelay + (kids.length - 1) * delay + duration + 100;
    const timer = setTimeout(() => {
      kids.forEach((child) => (child.style.willChange = 'auto'));
    }, cleanup);

    return () => clearTimeout(timer);
  }, [isVisible, delay, duration, startDelay]);

  return ref;
};

export default useStagger;
